import { inject, Injectable } from '@angular/core';
import {
	CALCULATION_URL,
	CrudService,
	downloadFile,
	IMPORT_URL,
} from '@marmon/frontend/common';
import { collectionCount } from '@marmon/frontend/firebase/firestore';
import { NotificationApiService } from '@marmon/frontend/notification';
import { AlgoliaFetch } from '@marmon/frontend/pagination';
import { FileProgress, StorageService } from '@marmon/frontend/storage';
import { AlgoliaEntity } from '@marmon/shared/types/algolia';
import {
	Analysis,
	AnalysisCalculation,
	AnalysisCalculationPayload,
	CalculationId,
	CalculationType,
	DefaultCalculationType,
	Invoice,
	InvoicePlot,
	InvoiceRollup,
	InvoiceRollupAverage,
	InvoiceRollupAverageType,
	PercentileGroup,
	PercentileGrouping,
	PeriodProfit,
	ProfitTimePeriodGroup,
	RollupEntity,
	activeAnalysisAlgoliaFilters,
	activeAnalysisAlgoliaTagFilters,
} from '@marmon/shared/types/analysis';
import { EntityId } from '@marmon/shared/types/common';
import { getName, User } from '@marmon/shared/types/user';
import {
	collection,
	doc,
	DocumentReference,
	getDoc,
	getDocs,
	query,
	QueryConstraint,
	where,
} from 'firebase/firestore';
import { BehaviorSubject, of, take, tap } from 'rxjs';
import { createAnalysis, analysisCsvFileName } from '../utils';

export type LoadingCalculation = {
	startedAt: Date;
	endedAt: Date | null;
	calculation: AnalysisCalculation;
	erroredAt: Date | null;
};

export type FetchCollectionCountOptions = {
	analysisId: string;
	calculationId: string;
	entity: 'customers' | 'products';
	constraints: QueryConstraint[];
};

export type CalculationFetchResponse = {
	calculation: AnalysisCalculation | null;
	state: 'cached' | 'server' | 'error';
};

const DefaultLoadingCalculation = () => ({
	startedAt: new Date(),
	erroredAt: null,
	endedAt: null,
	calculationId: null,
});

@Injectable({ providedIn: 'root' })
export class AnalysisApiService extends CrudService<Analysis> {
	/**
	 * cached plots in the system saved by the calculation id
	 */
	readonly plots = new Map<string, InvoicePlot[]>();

	private readonly storageService = inject(StorageService);
	private readonly notificationService = inject(NotificationApiService);

	private readonly calculationUrl = inject(CALCULATION_URL);
	private readonly importUrl = inject(IMPORT_URL);

	/**
	 * cache the collection count for the given analysis and calculation id
	 * this is used to prevent multiple calls to the same collection count
	 * when the same analysis and calculation is being used.
	 */
	private readonly cachedCollectionCount = new Map<string, number>();

	/**
	 * `true` if there is a calculation loading in the view. A loading calculation can take some significant time
	 * so we want to try and move this to the background so users can still navigate around.
	 */
	readonly loadingCalculation$ =
		new BehaviorSubject<LoadingCalculation | null>(null);

	constructor() {
		super('analyses', { algoliaIndex: 'analyses' });
	}

	override async create(
		payload: Pick<Analysis, 'name' | 'timePeriods' | 'costType'> & {
			currency?: Analysis['currency'];
		},
		options: {
			file: File;
			invoices: Invoice[];
			segments: string[];
		},
		callback?: (progress: FileProgress) => void,
	): Promise<Partial<Analysis>> {
		const user = this.user$.value;
		const userRef = this.userRef$.value;
		if (!user || !userRef)
			throw Error('Authenticated user could not be determined');

		const { segments, file } = options;

		const analysis = await super.create(
			createAnalysis(payload, {
				createdBy: userRef,
				domain: user.domain,
				segments: segments.sort((a, b) => (a > b ? 1 : -1)),
			}),
		);

		if (!analysis?.id) throw Error('Analysis was not created correctly');
		let csvStoragePath = '';
		await new Promise<void>((resolve) => {
			csvStoragePath = this.storageService.uploadCsv(
				file,
				{ id: analysis.id as string, userId: user.id },
				(progress) => {
					callback?.(progress);
					if (progress.complete) resolve();
				},
			);
		});

		await this.update(
			{ id: analysis.id, csvStoragePath },
			{ addActivityLog: false },
		);

		// csvStoragePath means later calculations parse this CSV. Invoice
		// docs written during import must use createInvoiceId() so those
		// calcs can patch quads/calculations onto the same ids.

		// Storage upload is the file transfer. Processing runs on the 60-minute
		// HTTP function; do not wait for it so the dialog can close.
		this.startCsvImport(analysis.id, csvStoragePath);

		this.clearAlgoliaCache();
		this.changed$.next();
		return analysis;
	}

	/**
	 * Starts server-side CSV processing without blocking the caller.
	 * The request stays subscribed on this root service so navigating away
	 * from the create dialog does not abort it.
	 */
	private startCsvImport(analysisId: string, storagePath: string) {
		this.http
			.post(`${this.importUrl}/v1/import/analysis/${analysisId}`, {
				storagePath,
			})
			.subscribe({
				error: (error) => {
					console.error(
						'AnalysisApiService.startCsvImport',
						analysisId,
						error,
					);
				},
			});
	}

	/**
	 * Fetches the requested calculation from the analysis provided. If no id is given
	 * then the default calculation will be returned from the analysis.
	 *
	 * @note it is better to omit the id if you are just fetching the default calculation instead
	 * of providing the id of default calculation.
	 *
	 * @param analysis the analysis to fetch the calculation of
	 * @param @optional id the id of the calculation fetch
	 */
	fetchCalculation(options: {
		analysis: Analysis | string;
		id?: string;
		type?: CalculationType;
		segmentSequence?: string | null;
	}): Promise<AnalysisCalculation | null> {
		const { analysis, id, type, segmentSequence } = {
			type: DefaultCalculationType,
			segmentSequence: null,
			...options,
		};

		const analysisId =
			typeof analysis === 'string' ? analysis : analysis.id;

		const docRef = doc(this.collection, analysisId);
		const calcCollection = collection(docRef, 'calculations');
		if (id) {
			return getDoc(doc(calcCollection, id)).then((res) => {
				if (!res || !res.exists()) return null;
				return res.data() as AnalysisCalculation;
			});
		}

		if (segmentSequence || type !== DefaultCalculationType) {
			return getDocs(
				query(
					calcCollection,
					where('segmentSequence', '==', segmentSequence),
					where('type', '==', type),
				),
			).then((res) => {
				if (!res || res.empty) return null;
				// there should be only one, but the error to inform us if that is not the case
				if (res.size > 1) {
					console.error(
						'Multiple existing calculations with the same sequence',
						analysisId,
						segmentSequence,
					);
				}
				return res.docs[0].data() as AnalysisCalculation;
			});
		}

		if (typeof analysis === 'string')
			return Promise.reject(
				'Default calculation could not be determined',
			);

		return getDoc(
			analysis.defaultCalculation as DocumentReference<AnalysisCalculation>,
		).then((res) => {
			if (!res.exists())
				throw Error('The default calculation does not exist');
			return res.data() as AnalysisCalculation;
		});
	}

	async findOrCreateCalculation(options: {
		analysis: Analysis;
		id?: string;
		type?: CalculationType;
		segmentSequence?: string | null;
	}): Promise<CalculationFetchResponse> {
		options = Object.assign(
			{ type: DefaultCalculationType, segmentSequence: null },
			options,
		);

		const calculation = await this.fetchCalculation(options).catch(
			() => null,
		);

		if (calculation) return { calculation, state: 'cached' };

		// only start the calculation loading if we don't have it cached already
		this.updateCalculationLoading({ startedAt: new Date() }, true);
		return new Promise((resolve, reject) => {
			this.http
				.post<AnalysisCalculation>(
					`${this.calculationUrl}/v1/calculate/analysis/${options.analysis.id}`,
					{
						type: options.type,
						segmentSequence: options.segmentSequence,
					} as AnalysisCalculationPayload,
				)
				.pipe(take(1))
				.subscribe({
					error: (error) => {
						this.updateCalculationLoading({
							erroredAt: new Date(),
							endedAt: new Date(),
						});
						reject(error);
					},
					next: (calculation) => {
						this.updateCalculationLoading({
							calculation,
							endedAt: new Date(),
						});
						resolve({ calculation, state: 'server' });
					},
				});
		});
	}

	/**
	 * Updates the calculation loading behavior subject with the partial data combined
	 * with the existing value. IF reset is requested we will use the new data with the
	 * default data instead.
	 * @param data the data to update the loader with
	 * @param reset `true` if the data should be reset back to default
	 */
	updateCalculationLoading(
		data: Partial<LoadingCalculation> | null,
		reset = false,
	) {
		if (data === null) {
			this.loadingCalculation$.next(null);
			return;
		}

		const value = reset
			? DefaultLoadingCalculation()
			: (this.loadingCalculation$.value ?? DefaultLoadingCalculation());

		this.loadingCalculation$.next({
			...value,
			...data,
		} as LoadingCalculation);
	}

	/**
	 * Determines what the count of the total fetch would be for the given parameters
	 *
	 * @param options the options required to determine the collection count
	 */
	fetchCollectionCount(options: FetchCollectionCountOptions) {
		const { analysisId, calculationId, entity, constraints } = options;

		const stringedOptions = JSON.stringify(options);
		const cachedCollectionCount =
			this.cachedCollectionCount.get(stringedOptions);
		if (cachedCollectionCount) return of(cachedCollectionCount);

		const analysisRef = doc(this.collection, analysisId);
		const calculationRef = doc(
			collection(analysisRef, 'calculations'),
			calculationId,
		);

		return collectionCount(
			query(collection(calculationRef, entity), ...constraints),
		).pipe(
			tap((value) => {
				this.cachedCollectionCount.set(stringedOptions, value);
			}),
		);
	}

	fetchPlots(options: {
		analysisId: EntityId;
		calculationId: CalculationId;
	}) {
		const { analysisId, calculationId } = options;

		const key = `${analysisId}-${calculationId}`;
		if (this.plots.has(key)) {
			return Promise.resolve(this.plots.get(key) as InvoicePlot[]);
		}

		const analysisRef = doc(this.collection, analysisId);
		const calculationRef = doc(
			collection(analysisRef, 'calculations'),
			calculationId,
		);

		return getDocs(collection(calculationRef, 'plots')).then((res) => {
			const plots = res.docs.map((d) => d.data() as InvoicePlot);
			this.plots.set(key, plots);
			return plots;
		});
	}

	async downloadCsv(analysis: Analysis) {
		// Only analyses with csvStoragePath retained a Storage object.
		if (!analysis.csvStoragePath) {
			throw new Error('This analysis does not have a stored CSV');
		}

		const blob = await this.storageService.getObjectBlob(
			analysis.csvStoragePath,
		);
		downloadFile(blob, analysisCsvFileName(analysis.name));
	}

	findCreator(analysis: Pick<Analysis, 'createdBy'>): Promise<User> {
		return getDoc(analysis.createdBy as DocumentReference<User>).then(
			(snap) => {
				if (!snap.exists()) {
					throw new Error('Analysis creator was not found');
				}
				return snap.data() as User;
			},
		);
	}

	/**
	 * Overridden from the base class because this instance should not provide the
	 * user's domain. This is strictly fetching from only what the user has
	 * access to view.
	 *
	 * @param options options to fetch from algolia
	 */
	override fetchAlgolia<AlgoliaType extends AlgoliaEntity = AlgoliaEntity>(
		options: Omit<AlgoliaFetch<AlgoliaType>, 'index'>,
	) {
		const user = this.user$.value;
		if (!user) throw Error('AnalysisApiService: User not authenticated');

		return super.fetchAlgolia({
			...options,
			id: user.id,
			filters: activeAnalysisAlgoliaFilters(Date.now(), options.filters),
			tagFilters: activeAnalysisAlgoliaTagFilters(
				options.tagFilters ?? [],
			),
		});
	}

	findRollup(options: {
		analysisId: string;
		calculationId: string;
		name: string;
		key: RollupEntity;
	}) {
		const { analysisId, calculationId, name, key } = options;
		const analysisRef = doc(this.collection, analysisId);
		const calculationRef = doc(
			collection(analysisRef, 'calculations'),
			calculationId,
		);

		const entityCollection = collection(calculationRef, `${key}s`);
		return getDocs(query(entityCollection, where(key, '==', name))).then(
			(snapshot) => {
				if (snapshot.empty) return null;
				return snapshot.docs[0].data() as InvoiceRollup;
			},
		);
	}

	async shareAnalysisById(options: {
		analysisId: string;
		userId: string;
	}): Promise<Analysis> {
		const { analysisId, userId } = options;
		const analysis = await this.findById(analysisId);

		const shared = new Map<string, DocumentReference<User>>();
		analysis.shared.forEach((s) =>
			shared.set(s.id, s as DocumentReference<User>),
		);

		if (shared.has(userId))
			throw Error('User already has been shared with');

		const userRef = doc(
			collection(this.firestore, 'users'),
			userId,
		) as DocumentReference<User>;
		shared.set(userId, userRef);

		return this.update(
			{ id: analysis.id, shared: Array.from(shared.values()) },
			{ addActivityLog: true, type: 'share' },
		).then((res) => {
			const user = this.user$.value;
			if (user) {
				this.notificationService.send(
					{
						type: 'user-shared-analysis',
						email: true,
						args: {
							id: analysisId,
							user: getName(user),
							name: analysis.name,
						},
					},
					userId,
				);
			}

			this.clearAlgoliaCache();
			return { ...analysis, ...res } as Analysis;
		});
	}

	async removeShareAnalysisById(options: {
		analysisId: string;
		userId?: string;
	}): Promise<Analysis> {
		if (!options.userId) {
			const user = this.user$.value;
			if (!user) throw Error('No user was found');
			options.userId = user.id;
		}

		const { analysisId, userId } = options;
		const analysis = await this.findById(analysisId);

		const shared = new Map<string, DocumentReference<User>>();
		analysis.shared.forEach((s) =>
			shared.set(s.id, s as DocumentReference<User>),
		);

		if (!shared.has(userId)) throw Error('User does not have share access');
		shared.delete(userId);

		return this.update(
			{ id: analysis.id, shared: Array.from(shared.values()) },
			{ addActivityLog: true, type: 'revoke-share' },
		).then((res) => {
			this.clearAlgoliaCache();
			return { ...analysis, ...res } as Analysis;
		});
	}

	findCalculationPercentile(options: {
		analysisId: EntityId;
		calculationId: CalculationId;
		grouping: PercentileGrouping;
	}): Promise<PercentileGroup | null> {
		const { analysisId, calculationId, grouping } = options;
		const analysisRef = doc(this.collection, analysisId);
		const calculationRef = doc(
			collection(analysisRef, 'calculations'),
			calculationId,
		);

		const percentileCollection = collection(calculationRef, 'percentiles');
		const percentileRef = doc(percentileCollection, grouping);
		return getDoc(percentileRef).then((res) => {
			if (!res || !res.exists()) return null;
			return res.data() as PercentileGroup;
		});
	}

	findCalculationEntityAverage(options: {
		calculationId: string;
		analysisId: string;
		averageType: InvoiceRollupAverageType;
	}): Promise<InvoiceRollupAverage | null> {
		const { calculationId, analysisId, averageType } = options;
		const analysisRef = doc(this.collection, analysisId);
		const calculationRef = doc(
			collection(analysisRef, 'calculations'),
			calculationId,
		);

		const entityCollection = collection(calculationRef, 'averages');
		const entityRef = doc(entityCollection, averageType);
		return getDoc(entityRef).then((res) => {
			if (!res || !res.exists()) return null;
			return res.data() as InvoiceRollupAverage;
		});
	}

	fetchCalculationEntityPeriods<T = PeriodProfit>(options: {
		type: 'quad' | 'quartile';
		calculationId: string;
		analysisId: string;
	}) {
		const { calculationId, analysisId, type } = options;
		const analysisRef = doc(this.collection, analysisId);
		const calculationRef = doc(
			collection(analysisRef, 'calculations'),
			calculationId,
		);

		const entityCollection = collection(calculationRef, 'periods');
		return getDocs(query(entityCollection, where('type', '==', type))).then(
			(res) => {
				if (!res || res.empty) return [];
				return res.docs.map((d) => d.data() as T);
			},
		);
	}

	findCalculationEntityPeriod<T = PeriodProfit>(options: {
		key: ProfitTimePeriodGroup;
		calculationId: string;
		analysisId: string;
	}) {
		const { calculationId, analysisId, key } = options;
		const analysisRef = doc(this.collection, analysisId);
		const calculationRef = doc(
			collection(analysisRef, 'calculations'),
			calculationId,
		);

		const entityCollection = collection(calculationRef, 'periods');
		return getDoc(doc(entityCollection, key)).then((res) => {
			if (!res || !res.exists()) throw Error('Period not found');
			return res.data() as T;
		});
	}
}
